// src/components/ChooseAssessmentTypeModal/ChooseAssessmentTypeModal.tsx
import React, { useContext, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Stack,
  Typography,
  Button,
  ButtonBase,
  Paper,
} from '@mui/material';
import { Check, ArrowRight, FileText } from 'lucide-react';
import {
  HandShakeIcons,
  UserCheckIcons,
  UsersIcons,
} from '../../../../icons/icons';
import { AssessmentContext } from '../../../../utils/contexts/contexts';
import {
  FeatureNames,
  Features,
} from '../../../../utils/helpers/subscriptionEnum';
import PermissionGate from '../../../../utils/hooks/PermissionGate';
import { ASSESSMENT_TYPE } from '../Constants/AssessmentTypes';

export type AssessmentType =
  | typeof ASSESSMENT_TYPE[keyof typeof ASSESSMENT_TYPE]
  | null;

interface ChooseAssessmentTypeModalProps {
  open: boolean;
  onClose: () => void;
  onSuccessClose: () => void;
}

const assessmentOptions: {
  value: AssessmentType;
  label: string;
  icon: React.ReactNode;
  featureName: FeatureNames;
  requiredFeature: Features;
  description: string;
}[] = [
  {
    value: ASSESSMENT_TYPE.CANDIDATES,
    label: 'Job Candidates',
    icon: <UserCheckIcons size={40} />,
    featureName: FeatureNames.CANDIDATE,
    requiredFeature: Features.CANDIDATE,
    description: 'Evaluate potential hires',
  },
  {
    value: ASSESSMENT_TYPE.TEAM_MEMBERS,
    label: 'Team Members',
    icon: <UsersIcons size={40} />,
    featureName: FeatureNames.EMPLOYEE,
    requiredFeature: Features.EMPLOYEE,
    description: 'Assess current employees',
  },
  {
    value: ASSESSMENT_TYPE.PARTNERS,
    label: 'Partners',
    icon: <HandShakeIcons size={40} />,
    featureName: FeatureNames.PARTNER,
    requiredFeature: Features.PARTNER,
    description: 'Evaluate business partners',
  },
];

export const ChooseAssessmentTypeModal: React.FC<ChooseAssessmentTypeModalProps> = ({
  open,
  onClose,
  onSuccessClose,
}) => {
  const { assessmentState, setAssessmentState } = useContext(AssessmentContext);
  const [selectedType, setSelectedType] = useState<AssessmentType | null>(null);

  const applySelection = () => {
    if (!selectedType) return;
    setAssessmentState({
      ...assessmentState,
      assessmentType: selectedType,
    });
    onSuccessClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth='md'
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          p: 2,
        },
      }}
    >
      <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
        <Stack alignItems='center' spacing={1.5}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              bgcolor: 'success.main',
              color: 'common.white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: (theme) => `0 4px 14px ${theme.palette.success.light}`,
            }}
          >
            <FileText size={28} />
          </Box>
          <Typography variant='h5' fontWeight={700}>
            Choose Assessment Type
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            Select the type of assessment you&apos;d like to create for your organization
          </Typography>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ py: 3 }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
            gap: 2.5,
            pt: 1,
          }}
        >
          {assessmentOptions.map(
            ({ value, label, icon, requiredFeature, description }) => {
              const isSelected = selectedType === value;
              return (
                <PermissionGate
                  key={value ?? label}
                  requiredPermissions={[]}
                  requiredFeature={requiredFeature}
                >
                  <ButtonBase
                    component='div'
                    onClick={() => setSelectedType(value)}
                    sx={{
                      width: '100%',
                      height: '100%',
                      display: 'block',
                      textAlign: 'center',
                      borderRadius: 3,
                    }}
                  >
                    <Paper
                      variant='outlined'
                      sx={{
                        position: 'relative',
                        p: 3,
                        height: '100%',
                        borderRadius: 3,
                        display: 'flex',
                        flexDirection: 'col',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.2s ease-in-out',
                        borderColor: isSelected ? 'success.main' : 'divider',
                        bgcolor: isSelected ? 'success.50' : 'background.paper',
                        boxShadow: isSelected ? 3 : 0,
                        '&:hover': {
                          borderColor: isSelected ? 'success.main' : 'success.light',
                          transform: 'translateY(-2px)',
                          boxShadow: 2,
                        },
                      }}
                    >
                      {/* Selection Check Circle */}
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 10,
                          right: 10,
                          width: 24,
                          height: 24,
                          borderRadius: '50%',
                          border: '2px solid',
                          borderColor: isSelected ? 'success.main' : 'divider',
                          bgcolor: isSelected ? 'success.main' : 'transparent',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'common.white',
                        }}
                      >
                        {isSelected && <Check size={14} />}
                      </Box>

                      {/* Icon */}
                      <Box
                        sx={{
                          mb: 2,
                          p: 2,
                          borderRadius: 2,
                          bgcolor: isSelected ? 'common.white' : 'action.hover',
                          color: isSelected ? 'success.main' : 'text.secondary',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        {icon}
                      </Box>

                      <Typography
                        variant='subtitle1'
                        fontWeight={600}
                        color={isSelected ? 'success.dark' : 'text.primary'}
                      >
                        {label}
                      </Typography>

                      <Typography variant='caption' color='text.secondary' sx={{ mt: 0.5 }}>
                        {description}
                      </Typography>
                    </Paper>
                  </ButtonBase>
                </PermissionGate>
              );
            },
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, pt: 1, justifyContent: 'space-between' }}>
        <Button onClick={onClose} variant='outlined' color='inherit'>
          Cancel
        </Button>
        <Button
          onClick={applySelection}
          disabled={!selectedType}
          variant='contained'
          color='success'
          endIcon={<ArrowRight size={16} />}
          sx={{ minWidth: 140 }}
        >
          {selectedType ? 'Continue' : 'Select a type'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
